import React from "react";
import moment from "moment";
import userImage from "../../../files/user.png";

const capitalizeFirstLetter = (str) => {
  if (!str || typeof str !== "string") return "-";
  return str
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const formatName = (name) => {
  if (!name || typeof name !== "string") return "-";
  return name.split(",").map((part) => capitalizeFirstLetter(part.trim())).join(", ");
};

const toCapitalizedEmail = (value) => {
  if (!value || typeof value !== "string") {
    return "-";
  }
  return capitalizeFirstLetter(value);
};

const getOrganizationIcon = (organization) => {
  if (!organization) return <i className="mdi mdi-hospital-building"></i>;
  const orgLower = organization.toLowerCase();
  if (orgLower.includes("hospital") || orgLower.includes("medical")) {
    return <i className="mdi mdi-hospital-building"></i>;
  }
  if (orgLower.includes("clinic")) {
    return <i className="mdi mdi-medical-clinic"></i>;
  }
  if (orgLower.includes("health") || orgLower.includes("wellness")) {
    return <i className="mdi mdi-heart-pulse"></i>;
  }
  if (orgLower.includes("care")) {
    return <i className="mdi mdi-hand-heart"></i>;
  }
  return <i className="mdi mdi-hospital-building"></i>;
};

const ComponentCPTCell = ({ cptCodes, componentType }) => {
  if (!cptCodes || typeof cptCodes !== "object") return "-";

  const codes = Object.entries(cptCodes);
  if (codes.length === 0) return "-";

  const groupedSections = codes.reduce((acc, [key, details]) => {
    if (!details) return acc;
    const sectionKey = details.componentType || "other";
    if (!acc[sectionKey]) {
      acc[sectionKey] = {
        key: sectionKey,
        label: details.componentLabel || details.name || sectionKey,
        isMet: details.componentMet === true,
        count: Number(details.componentCount || 0),
        rows: []
      };
    }
    acc[sectionKey].rows.push({ key, details });
    return acc;
  }, {});

  const section = groupedSections[componentType];
  if (!section) return "-";
  const normalizedSectionLabel = section.key === "technical"
    ? "Technical"
    : (section.key === "professional" ? "Professional" : section.label);

  return (
    <div className="cpt-details-cell component-sections">
      <div key={section.key} className="component-section-block">
        <div className="component-section-header">
          <span className="component-section-title">
            {normalizedSectionLabel}
          </span>
          <span className={`component-section-status ${section.isMet ? "met" : "not-met"}`}>
            {section.isMet ? "MET" : "NOT MET"}
          </span>
        </div>
        <div className="component-codes-wrap">
          {section.rows.map(({ key, details }) => {
            const isMet = details.met === true;
            const displayCode = details.code || key || "-";
            return (
              <span key={key} className={`cpt-code-badge ${isMet ? "met" : "not-met"}`}>
                {displayCode}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const PatientNameCell = ({ name, email, phone, profileImage, isActive, onViewPatient }) => (
  <div className="patient-profile-cell">
    <img src={profileImage || userImage} alt={formatName(name) || "Patient"} className="patient-profile-avatar" />
    <div className="patient-profile-content">
      <div className="patient-profile-name-row">
        <button type="button" className="patient-profile-name-link" onClick={onViewPatient}>
          {formatName(name)}
        </button>
        <span className={`patient-active-tag ${isActive ? "active" : "inactive"}`}>
          {isActive ? "Active" : "Inactive"}
        </span>
      </div>
      <div className="patient-profile-meta">
        <i className="mdi mdi-email-outline"></i>
        <span>{toCapitalizedEmail(email)}</span>
      </div>
      <div className="patient-profile-meta">
        <i className="mdi mdi-phone-outline"></i>
        <span>{phone || "-"}</span>
      </div>
    </div>
  </div>
);

const DateCell = ({ date, iconClass }) => (
  <div className="date-cell-wrapper">
    <i className={`mdi ${iconClass} date-icon`}></i>
    <span className="date-text">{date || "-"}</span>
  </div>
);

const CareProviderCell = ({ provider }) => (
  provider && provider !== "-" ? <span className="date-text-display">{formatName(provider)}</span> : "-"
);

const OrganizationCell = ({ organization }) => (
  <div className="org-cell-wrapper">
    <span className="org-icon">{getOrganizationIcon(organization)}</span>
    <span className="org-text">{organization === "-" ? "-" : capitalizeFirstLetter(organization)}</span>
  </div>
);

const StatusCell = ({ status, label }) => (
  <span className={`patient-status-badge ${status === "met" ? "met" : "not-met"}`}>{label}</span>
);

const PatientActionCell = ({ onViewReport, onViewDetails }) => (
  <div className="patient-action-cell-wrap">
    <button type="button" className="patient-details-btn patient-details-btn-report" onClick={onViewReport}>
      <i className="mdi mdi-file-document-outline"></i>
      <span>View Report</span>
    </button>
    <button type="button" className="patient-details-btn patient-details-btn-profile" onClick={onViewDetails}>
      <i className="mdi mdi-account-details-outline"></i>
      <span>Details</span>
    </button>
  </div>
);

export const getNonAdherentPatientListColumns = ({ onViewReport, onViewDetails } = {}) => [
  {
    dataField: "patientName",
    text: "Last, First Name",
    sort: true,
    sortValue: (cell) => String(cell || "").toLowerCase(),
    formatter: (cell, row) => (
      <PatientNameCell
        name={cell}
        email={row?.email}
        phone={row?.phone}
        profileImage={row?.profileImage}
        isActive={row?.isActive}
        onViewPatient={() => onViewReport?.(row)}
      />
    ),
    headerStyle: () => ({ minWidth: "250px" })
  },
  {
    dataField: "enrolledDateFormatted",
    text: "Enrolled Date",
    sort: true,
    sortValue: (cell, row) => row?.enrolledDateValue || "",
    formatter: (cell) => <DateCell date={cell} iconClass="mdi-calendar-outline" />
  },
  {
    dataField: "lastUploadDateFormatted",
    text: "Last Uploaded Date",
    sort: true,
    sortValue: (cell, row) => row?.lastUploadDateValue || "",
    formatter: (cell) => <DateCell date={cell} iconClass="mdi-calendar-upload" />
  },
  {
    dataField: "mainCareProviderName",
    text: "Main Care Provider",
    sort: true,
    formatter: (cell) => <CareProviderCell provider={cell} />
  },
  {
    dataField: "organization",
    text: "Organization",
    sort: true,
    formatter: (cell) => <OrganizationCell organization={cell} />
  },
  {
    dataField: "technicalCptDetails",
    text: "Technical CPT",
    sort: true,
    formatter: (cell, row) => <ComponentCPTCell cptCodes={row?.cptCodes} componentType="technical" />,
    headerStyle: () => ({ minWidth: "220px" })
  },
  {
    dataField: "professionalCptDetails",
    text: "Professional CPT",
    sort: true,
    formatter: (cell, row) => <ComponentCPTCell cptCodes={row?.cptCodes} componentType="professional" />,
    headerStyle: () => ({ minWidth: "250px" })
  },
  {
    dataField: "patientActions",
    text: "Action",
    isDummyField: true,
    csvExport: false,
    formatter: (cell, row) => (
      <PatientActionCell
        onViewReport={() => onViewReport?.(row)}
        onViewDetails={() => onViewDetails?.(row)}
      />
    ),
    headerStyle: () => ({ minWidth: "130px", textAlign: "center" }),
    headerClasses: "text-center",
    classes: "text-center"
  },
  {
    dataField: "statusLabel",
    text: "Status",
    sort: true,
    formatter: (cell, row) => <StatusCell status={row?.status} label={cell} />
  }
];

export const processNonAdherentPatientData = (patients = []) =>
  patients.map((patient) => {
    const additionalDetails = patient?.additionalDetails || {};
    const additionalDetailsText = Object.keys(additionalDetails)
      .filter((key) => additionalDetails[key] !== undefined && additionalDetails[key] !== null && additionalDetails[key] !== "")
      .map((key) => `${key.replace(/([A-Z])/g, " $1").trim()}: ${additionalDetails[key]}`)
      .join(" | ");

    const profileImage = patient?.profileImage
      ? `${process.env.REACT_APP_VIDEO_CALL_REQUEST}/${patient.profileImage}`
      : userImage;

    const lastUploadDateValue = patient?.lastUploadDate || "";
    const lastUploadDateFormatted = lastUploadDateValue
      ? (moment(lastUploadDateValue).isValid() ? moment(lastUploadDateValue).format("MMM DD, YYYY") : lastUploadDateValue)
      : "-";
    const enrolledDateValue = patient?.enrolledDate || "";
    const enrolledDateFormatted = enrolledDateValue
      ? (moment(enrolledDateValue).isValid() ? moment(enrolledDateValue).format("MMM DD, YYYY") : enrolledDateValue)
      : "-";

    return {
      ...patient,
      patientName: formatName(patient?.patientName),
      profileImage,
      organization: patient?.client?.organization || "-",
      mainCareProviderName: formatName(patient?.mainCareProvider?.name),
      isActive: Boolean(patient?.isActive),
      lastUploadDateValue,
      lastUploadDateFormatted,
      enrolledDateValue,
      enrolledDateFormatted,
      technicalCptDetails: additionalDetails?.technicalCodes || "-",
      professionalCptDetails: additionalDetails?.professionalCodes || "-",
      additionalDetailsText: additionalDetailsText || "-",
      statusLabel: patient?.status === "met" ? "MET" : patient?.status === "notMet" ? "NOT MET" : (patient?.status || "-").toUpperCase()
    };
  });
